import { Array, Promise$1, FontType, uniq } from './main.js';

const isKeyOf = (obj, key) => key in obj;
const defaultTracerName = 'agentTracer';
const currentSpanKey = Symbol('thn-span');
const parentSpanKey = Symbol('thn-parent-span');
const globalRequirements = {};
const activeSpans = [];
function getCurrentSpan(args) {
    const argsArray = Array.isArray(args) ? args : [...args];
    const injectedArg = argsArray.find(arg => arg && arg[currentSpanKey]);
    return injectedArg ? injectedArg[currentSpanKey] : null;
}
function getParentSpan(args) {
    const argsArray = Array.isArray(args) ? args : [...args];
    const injectedArg = argsArray.find(arg => arg && arg[parentSpanKey]);
    return injectedArg ? injectedArg[parentSpanKey] : null;
}
function getTracerProviderAndContext(instance) {
    const getTracerProvider = instance.container?.trace?.getTracerProvider || globalRequirements.trace?.getTracerProvider;
    const getContext = instance.container?.context?.active || globalRequirements.context?.active;
    if (!getTracerProvider) {
        return 'trace provider';
    }
    if (!getContext) {
        return 'span context';
    }
    const tracerProvider = getTracerProvider();
    const spanContext = getContext();
    return { tracerProvider, spanContext };
}
const shouldSkipTracing = (options, spanName) => options.methods && options.methods.length && !options.methods.includes(spanName);
const execute = (originalFunction, args, containerArg, options = {}) => {
    const requirements = getTracerProviderAndContext(containerArg);
    if (typeof requirements === 'string') {
        return originalFunction.apply(containerArg, args);
    }
    const parentSpanId = getParentSpan(args)?.spanContext().spanId || activeSpans[activeSpans.length - 1]?.spanContext().spanId;
    if (shouldSkipTracing(options, originalFunction.name)) {
        return originalFunction.apply(containerArg, args);
    }
    const { tracerProvider, spanContext } = requirements;
    const tracer = tracerProvider.getTracer(options.tracerName || defaultTracerName);
    spanContext.setValue(Symbol.for('parentSpanId'), parentSpanId);
    const spanName = options.name || originalFunction.name;
    const span = tracer.startSpan(spanName, undefined, spanContext);
    activeSpans.push(span);
    args.push({ [currentSpanKey]: span });
    try {
        const result = originalFunction.apply(containerArg, args);
        if (result instanceof Promise$1) {
            return result.finally(() => {
                activeSpans.pop();
                span.end();
            });
        }
        activeSpans.pop();
        span.end();
        return result;
    }
    catch (error) {
        activeSpans.pop();
        span.end();
        throw error;
    }
};
function traceClass(classOptions = {}) {
    return function decorator(constructor) {
        return class TracedClass extends constructor {
            constructor(...constructorArgs) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                super(...constructorArgs);
                const options = {
                    ...classOptions,
                    name: `${constructor.name}.constructor`,
                };
                const containerArg = this;
                const requirements = getTracerProviderAndContext(containerArg);
                if (typeof requirements === 'string') ;
                else {
                    const { tracerProvider, spanContext } = requirements;
                    const tracer = tracerProvider.getTracer(options.tracerName || defaultTracerName);
                    const spanName = options.name || constructor.name;
                    const span = tracer.startSpan(spanName, undefined, spanContext);
                    activeSpans.push(span);
                    try {
                        activeSpans.pop();
                        span.end();
                    }
                    catch (error) {
                        activeSpans.pop();
                        span.end();
                        throw error;
                    }
                }
                // eslint-disable-next-line no-constructor-return
                return new Proxy(this, {
                    get(target, prop) {
                        if (!isKeyOf(target, prop)) {
                            return target[prop];
                        }
                        if (typeof target[prop] !== 'function') {
                            return target[prop];
                        }
                        const methodOptions = {
                            ...classOptions,
                            name: `${constructor.name}.${String(prop)}`,
                        };
                        const originalMethod = target[prop];
                        function replacementMethod(...args) {
                            return execute(originalMethod, args, this, methodOptions);
                        }
                        return replacementMethod;
                    },
                });
            }
        };
    };
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __esDecorate(ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
}
function __runInitializers(thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
}

class Provider {
    kind;
    static baseUrl = '';
    constructor(kind) {
        this.kind = kind;
    }
    getUrls(fontsData, families) {
        const providerFonts = this.filterByFontKind(fontsData);
        if (!providerFonts || !providerFonts.length) {
            return [];
        }
        if (!families || !families.length) {
            return [];
        }
        const fontsToProcess = this.filterByFamilies(providerFonts, families);
        if (!fontsToProcess || !fontsToProcess.length) {
            return [];
        }
        return [this.getUrl(fontsToProcess)];
    }
    filterByFontKind(fontsData) {
        return fontsData.filter((font) => font.kind === this.kind);
    }
    // eslint-disable-next-line class-methods-use-this
    filterByFamilies(fontsData, families) {
        return fontsData.filter((font) => families.includes(font.family));
    }
}

class FileProvider extends Provider {
    static baseUrl = 'https://www.thehotelsnetwork.com/_/fonts';
    constructor() {
        super(FontType.File);
    }
    // eslint-disable-next-line class-methods-use-this
    getUrl(fontsData) {
        const keys = uniq(fontsData.map((data) => data.key));
        return `${FileProvider.baseUrl}?family=${keys.join(',')}`;
    }
}

class GoogleProvider extends Provider {
    static baseUrl = 'https://www.thehotelsnetwork.com/gfonts/css2';
    static variants = ':wght@400;700';
    constructor() {
        super(FontType.Google);
    }
    // eslint-disable-next-line class-methods-use-this
    getUrl(fontsData) {
        const keys = uniq(fontsData.map((data) => data.key));
        return `${GoogleProvider.baseUrl}?family=${keys.join(`${GoogleProvider.variants}&family=`)}${GoogleProvider.variants}`;
    }
}

let FontsLoaderService = (() => {
    let _classDecorators = [traceClass()];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    (class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        container;
        loadedFamilies = [];
        constructor(container) {
            this.container = container;
        }
        async addFonts(fontsData, document) {
            const families = this.getFamilies(fontsData);
            const documentFamilies = this.documentFamilies(document);
            const loadedFamilies = [...this.loadedFamilies, ...documentFamilies];
            const familiesToLoad = this.exceptFamilies(families, loadedFamilies);
            // eslint-disable-next-line prefer-rest-params
            const span = getCurrentSpan(arguments);
            span?.setAttribute('families', families);
            if (!familiesToLoad.length) {
                return;
            }
            await this.loadFonts(familiesToLoad, fontsData, document);
            this.loadedFamilies = [...this.loadedFamilies, ...familiesToLoad];
        }
        // eslint-disable-next-line class-methods-use-this
        documentFamilies(document) {
            const families = [];
            document.fonts?.forEach((font) => {
                const family = font.family.trim().replace(/"/g, '');
                if (!families.includes(family)) {
                    families.push(family);
                }
            });
            return families;
        }
        // eslint-disable-next-line class-methods-use-this
        getFamilies(fonts) {
            return fonts.map((font) => font.family);
        }
        // eslint-disable-next-line class-methods-use-this
        exceptFamilies(families, except) {
            return families.filter((family) => !except.includes(family));
        }
        async loadFonts(families, fontsData, document) {
            const googleUrls = new GoogleProvider().getUrls(fontsData, families);
            const fileUrls = new FileProvider().getUrls(fontsData, families);
            const urls = [...googleUrls, ...fileUrls];
            await Promise$1.all(urls.map((url) => this.appendLink(url, document)));
            await Promise$1.all(families.map((family) => this.loadFont(family, document)));
        }
        // eslint-disable-next-line class-methods-use-this
        async appendLink(url, document) {
            await new Promise$1((resolve) => {
                const el = document.createElement('link');
                el.href = url;
                el.rel = 'stylesheet';
                el.type = 'text/css';
                el.onload = () => {
                    resolve();
                };
                document.head.appendChild(el);
            });
        }
        // eslint-disable-next-line class-methods-use-this
        async loadFont(family, document) {
            // This is a "trick" to load a font: we draw a space with that font on an invisible canvas.
            // This forces the browser to load the font if it hasn't done so yet.
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return;
                }
                const fontStyle = `12px '${family}'`;
                ctx.font = fontStyle;
                ctx.fillText('', 0, 0);
                await document.fonts.load(fontStyle);
            }
            catch (err) {
                this.container.logger.warn('loadFonts()', `Error: ${err.message}`);
            }
        }
    });
    return _classThis;
})();

export { FontsLoaderService };
